import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useOffline } from "@/context/OfflineContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PhotoCapture from "@/components/PhotoCapture";
import InvestigationSteps from "@/components/InvestigationSteps";
import DiagnosticCompteur from "@/components/DiagnosticCompteur";
import SignaturePad from "@/components/SignaturePad";
import { 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  CheckCircle,
  User,
  Users,
  MapPin,
  Droplets,
  Camera,
  PenTool,
  WifiOff,
  FlaskConical
} from "lucide-react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import localforage from "localforage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Backward compat: normalize diagnostic_compteur_data (dict→[dict], null→null)
const normalizeDcData = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw;
  // Old single-object format → wrap in array
  return [{ ...raw, numero_fuite: 1, description: raw.description || "" }];
};

// Types de locaux
const LOCAL_TYPES = [
  { value: "logement_individuel", label: "Logement individuel" },
  { value: "appartement_immeuble", label: "Appartement / immeuble" },
  { value: "local_professionnel", label: "Local professionnel" },
];

// Techniques de détection de fuite
const DETECTION_TECHNIQUES = [
  "Aiguille détectable",
  "Caméra d'inspection",
  "Caméra endoscopique",
  "Caméra thermique",
  "Colorant et lampe UV",
  "Fumigène",
  "Gaz traceur",
  "Humidimètre",
  "Inspection visuelle",
  "Test d'aspersion en eau",
  "Test de pression",
  "Écoute acoustique",
];

// Types de résultat
const RESULT_TYPES = [
  { value: "fuite_constatee", label: "Fuite ou infiltration constatée" },
  { value: "pas_de_fuite", label: "Pas de fuite constatée" },
];

const BUILDING_TYPES = [
  { value: "maison", label: "Maison" },
  { value: "appartement", label: "Appartement" },
  { value: "commerce", label: "Commerce" },
  { value: "autre", label: "Autre" },
];

const LEAK_TYPES = [
  { value: "canalisation", label: "Canalisation" },
  { value: "infiltration", label: "Infiltration" },
  { value: "toiture", label: "Toiture" },
  { value: "appareil", label: "Appareil" },
  { value: "autre", label: "Autre" },
];

const ORIGIN_TYPES = [
  { value: "canalisation_eau_froide", label: "Canalisation eau froide" },
  { value: "canalisation_eau_chaude", label: "Canalisation eau chaude" },
  { value: "evacuation", label: "Évacuation" },
  { value: "joint_sanitaire", label: "Joint sanitaire" },
  { value: "appareil_menager", label: "Appareil ménager" },
  { value: "toiture", label: "Toiture" },
  { value: "terrasse", label: "Terrasse" },
  { value: "facade", label: "Façade" },
  { value: "autre", label: "Autre" },
];

const ORIGIN_LOCATIONS = [
  { value: "partie_a", label: "Partie A" },
  { value: "partie_b", label: "Partie B" },
  { value: "parties_communes", label: "Parties communes" },
  { value: "indeterminee", label: "Indéterminée" },
];

const PARTY_B_ROLES = [
  { value: "locataire", label: "Locataire ou occupant non propriétaire" },
  { value: "proprietaire", label: "Propriétaire / copropriétaire" },
  { value: "syndic", label: "Syndic" },
  { value: "gerant", label: "Gérant de l'immeuble" },
  { value: "autre", label: "Autre" },
];

const PARTY_B_RELATIONS = [
  { value: "Appartement sous-jacent", label: "Appartement sous-jacent" },
  { value: "Appartement au-dessus", label: "Appartement au-dessus" },
  { value: "Appartement voisin", label: "Appartement voisin" },
  { value: "Local commercial voisin", label: "Local commercial voisin" },
  { value: "Parties communes", label: "Parties communes" },
  { value: "Autre", label: "Autre" },
];

const PIECES_TOUCHEES = [
  { value: "Cuisine", label: "Cuisine" },
  { value: "Salle de bain", label: "Salle de bain" },
  { value: "WC", label: "WC" },
  { value: "Chambre", label: "Chambre" },
  { value: "Salon", label: "Salon" },
  { value: "Couloir", label: "Couloir" },
  { value: "Cave", label: "Cave" },
  { value: "Garage", label: "Garage" },
  { value: "Autre", label: "Autre" },
];

const ROOMS = [
  "Cuisine",
  "Salle de bain",
  "WC",
  "Chambre",
  "Salon",
  "Entrée",
  "Couloir",
  "Cave",
  "Garage",
  "Autre",
];

const MATERIALS = [
  "Plafond",
  "Mur",
  "Sol",
  "Isolation",
  "Plinthes",
  "Menuiseries",
  "Électricité",
  "Autre",
];

const HUMIDITY_LEVELS = [
  { value: "faible", label: "Faible (< 30%)" },
  { value: "moyen", label: "Moyen (30-60%)" },
  { value: "eleve", label: "Élevé (60-80%)" },
  { value: "tres_eleve", label: "Très élevé (> 80%)" },
];

const ClaimForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOnline, saveClaimLocally } = useOffline();
  const isEdit = !!id;

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasPartyB, setHasPartyB] = useState(false);

  const [formData, setFormData] = useState({
    // Client / Sinistré
    client_name: "",
    client_first_name: "",
    client_phone: "",
    client_email: "",
    client_insurance: "",
    client_contract_number: "",
    client_address: "",
    client_city: "",
    client_postal_code: "",
    
    // Partie B - Tiers concerné (optional)
    party_b_name: "",
    party_b_first_name: "",
    party_b_address: "",
    party_b_postal_code: "",
    party_b_city: "",
    party_b_phone: "",
    party_b_email: "",
    party_b_insurance: "",
    party_b_contract_number: "",
    party_b_role: "",
    party_b_role_other: "",
    party_b_relation: "",
    party_b_relation_other: "",
    party_b_piece_touchee: "",
    party_b_piece_touchee_other: "",
    
    // Intervention
    address: "",
    city: "",
    postal_code: "",
    local_type: "",
    intervention_date: "",
    report_writer: "",
    intervention_status: "",
    service_price: "",
    repair_responsibility: "",
    building_type: "appartement",
    
    // Description du sinistre
    sinister_description: "",
    
    // Numéro de facture
    invoice_number: "",
    
    // Techniques utilisées
    techniques_used: [],
    
    // Technical info (legacy)
    leak_origin: "",
    leak_type: "autre",
    origin_type: "",
    origin_location: "",
    affected_rooms: [],
    affected_materials: [],
    humidity_level: "",
    technical_observations: "",
    damage_date: "",
    
    // Résultat
    result_type: "",
    
    // Conclusion et préconisations
    conclusion: "",
    recommendations: "",
    
    // Media (legacy — kept for backward compatibility with old reports)
    photos: [],
    photo_captions: [],
    videos: [],
    documents: [],
    
    // Investigation steps (new structured format)
    investigations: [],
    
    // Diagnostic compteur (intervention après compteur)
    diagnostic_compteur: false,
    diagnostic_compteur_data: null,
    
    // Signatures
    party_a_signature: null,
    party_b_signature: null,
    technician_signature: null,
  });

  const [sameAddress, setSameAddress] = useState(false);

  const handleSameAddressToggle = (checked) => {
    setSameAddress(checked);
    if (checked) {
      updateField("address", formData.client_address || "");
      updateField("postal_code", formData.client_postal_code || "");
      updateField("city", formData.client_city || "");
    }
  };

  // Dynamic steps — swap investigation for diagnostic in compteur mode
  const getSteps = () => {
    const step4 = formData.diagnostic_compteur
      ? { id: "diagnostic", title: "Diagnostic", icon: FlaskConical }
      : { id: "investigation", title: "Investigation", icon: FlaskConical };
    
    return [
      { id: "client", title: "Client", icon: User },
      { id: "intervention", title: "Intervention", icon: MapPin },
      { id: "sinister", title: "Sinistre", icon: Droplets },
      step4,
      { id: "result", title: "Résultat", icon: CheckCircle },
      { id: "signatures", title: "Signatures", icon: PenTool }
    ];
  };
  
  const steps = getSteps();

  useEffect(() => {
    if (isEdit) {
      fetchClaim();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const fetchClaim = async () => {
    setLoading(true);
    try {
      let claim;
      
      // Try server first if online
      if (isOnline) {
        try {
          const response = await axios.get(`${API}/claims/${id}`);
          claim = response.data;
        } catch (e) {
          // Fallback to local storage
          const localClaims = await localforage.getItem("pending_claims") || [];
          claim = localClaims.find(c => c.id === id);
        }
      } else {
        // Offline: check local storage
        const localClaims = await localforage.getItem("pending_claims") || [];
        const cachedClaims = await localforage.getItem("cached_claims") || [];
        claim = localClaims.find(c => c.id === id) || cachedClaims.find(c => c.id === id);
      }
      
      if (!claim) {
        toast.error("Dossier non trouvé");
        navigate("/claims");
        return;
      }
      
      // Check if Party B exists
      const partyBExists = !!(claim.party_b_name || claim.party_b_first_name);
      setHasPartyB(partyBExists);
      
      setFormData({
        // Partie A
        client_name: claim.client_name || "",
        client_first_name: claim.client_first_name || "",
        client_phone: claim.client_phone || "",
        client_email: claim.client_email || "",
        client_insurance: claim.client_insurance || "",
        client_contract_number: claim.client_contract_number || "",
        client_address: claim.client_address || "",
        client_city: claim.client_city || "",
        client_postal_code: claim.client_postal_code || "",
        
        // Partie B
        party_b_name: claim.party_b_name || "",
        party_b_first_name: claim.party_b_first_name || "",
        party_b_address: claim.party_b_address || "",
        party_b_postal_code: claim.party_b_postal_code || "",
        party_b_city: claim.party_b_city || "",
        party_b_phone: claim.party_b_phone || "",
        party_b_email: claim.party_b_email || "",
        party_b_insurance: claim.party_b_insurance || "",
        party_b_contract_number: claim.party_b_contract_number || "",
        party_b_role: claim.party_b_role || "",
        party_b_role_other: claim.party_b_role_other || "",
        party_b_relation: claim.party_b_relation || "",
        party_b_relation_other: claim.party_b_relation_other || "",
        party_b_piece_touchee: claim.party_b_piece_touchee || "",
        party_b_piece_touchee_other: claim.party_b_piece_touchee_other || "",
        
        // Intervention
        address: claim.address || "",
        city: claim.city || "",
        postal_code: claim.postal_code || "",
        local_type: claim.local_type || "",
        intervention_date: claim.intervention_date || "",
        report_writer: claim.report_writer || "",
        intervention_status: claim.intervention_status || "",
        service_price: claim.service_price || "",
        repair_responsibility: claim.repair_responsibility || "",
        invoice_number: claim.invoice_number || "",
        building_type: claim.building_type || "appartement",
        
        // Description du sinistre
        sinister_description: claim.sinister_description || "",
        
        // Techniques utilisées
        techniques_used: claim.techniques_used || [],
        
        // Technical info (legacy)
        damage_date: claim.damage_date || "",
        leak_origin: claim.leak_origin || "",
        leak_type: claim.leak_type || "autre",
        origin_type: claim.origin_type || "",
        origin_location: claim.origin_location || "",
        affected_rooms: claim.affected_rooms || [],
        affected_materials: claim.affected_materials || [],
        humidity_level: claim.humidity_level || "",
        technical_observations: claim.technical_observations || "",
        
        // Résultat
        result_type: claim.result_type || "",
        
        // Conclusion et préconisations
        conclusion: claim.conclusion || "",
        recommendations: claim.recommendations || "",
        
        // Media (legacy)
        photos: claim.photos || [],
        photo_captions: claim.photo_captions || [],
        videos: claim.videos || [],
        documents: claim.documents || [],
        
        // Investigation steps
        investigations: claim.investigations || [],
        
        // Diagnostic compteur
        diagnostic_compteur: claim.diagnostic_compteur || false,
        diagnostic_compteur_data: normalizeDcData(claim.diagnostic_compteur_data),
        
        // Signatures (support both old and new field names)
        party_a_signature: claim.party_a_signature || claim.client_signature || null,
        party_b_signature: claim.party_b_signature || null,
        technician_signature: claim.technician_signature || null,
      });
    } catch (error) {
      toast.error("Erreur lors du chargement du dossier");
      navigate("/claims");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData((prev) => {
      const current = prev[field] || [];
      const newArray = current.includes(item)
        ? current.filter((i) => i !== item)
        : [...current, item];
      return { ...prev, [field]: newArray };
    });
  };

  const handleSave = async (complete = false) => {
    setSaving(true);
    try {
      const claimId = id || uuidv4();
      const now = new Date().toISOString();
      
      // Auto-derive techniques_used from investigations for backward compatibility
      const derivedTechniques = (formData.investigations || [])
        .filter(s => s.technique)
        .map(s => s.technique);
      
      const payload = {
        ...formData,
        techniques_used: derivedTechniques.length > 0 ? derivedTechniques : formData.techniques_used,
        id: claimId,
        status: complete ? "completed" : "in_progress",
        created_at: formData.created_at || now,
        updated_at: now,
      };

      if (isOnline) {
        try {
          if (isEdit) {
            await axios.put(`${API}/claims/${id}`, payload);
          } else {
            // Strip fields that are auto-managed by backend on create
            const { status, created_at, updated_at, ...createPayload } = payload;
            await axios.post(`${API}/claims`, createPayload);
          }
          toast.success(complete ? "Dossier finalisé" : "Dossier enregistré");
        } catch (error) {
          // Server error - save locally as fallback
          await saveClaimLocally(payload);
          toast.warning("Enregistré localement (erreur serveur)");
        }
      } else {
        // Save locally when offline
        await saveClaimLocally(payload);
        toast.success("Dossier enregistré localement - synchronisation automatique au retour du réseau");
      }

      navigate("/claims");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    const stepId = steps[currentStep]?.id;
    switch (stepId) {
      case "client":
        return formData.client_name.trim() !== "";
      case "intervention":
        return formData.address.trim() !== "";
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    const stepId = steps[currentStep]?.id;
    
    switch (stepId) {
      case "client":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-sky-800">Informations du client / sinistré</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="client_first_name">Prénom</Label>
                <Input
                  id="client_first_name"
                  value={formData.client_first_name}
                  onChange={(e) => updateField("client_first_name", e.target.value)}
                  placeholder="Prénom"
                  className="h-12 mt-1"
                  data-testid="input-client-first-name"
                />
              </div>
              <div>
                <Label htmlFor="client_name">Nom *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => updateField("client_name", e.target.value)}
                  placeholder="Nom"
                  className="h-12 mt-1"
                  data-testid="input-client-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="client_address">Adresse</Label>
              <Input
                id="client_address"
                value={formData.client_address}
                onChange={(e) => updateField("client_address", e.target.value)}
                placeholder="Numéro et rue"
                className="h-12 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="client_postal_code">Code postal</Label>
                <Input
                  id="client_postal_code"
                  value={formData.client_postal_code}
                  onChange={(e) => updateField("client_postal_code", e.target.value)}
                  placeholder="75000"
                  className="h-12 mt-1"
                />
              </div>
              <div>
                <Label htmlFor="client_city">Ville</Label>
                <Input
                  id="client_city"
                  value={formData.client_city}
                  onChange={(e) => updateField("client_city", e.target.value)}
                  placeholder="Paris"
                  className="h-12 mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="client_phone">Téléphone</Label>
              <Input
                id="client_phone"
                type="tel"
                value={formData.client_phone}
                onChange={(e) => updateField("client_phone", e.target.value)}
                placeholder="06 12 34 56 78"
                className="h-12 mt-1"
                data-testid="input-client-phone"
              />
            </div>
            <div>
              <Label htmlFor="client_email">Email</Label>
              <Input
                id="client_email"
                type="email"
                value={formData.client_email}
                onChange={(e) => updateField("client_email", e.target.value)}
                placeholder="email@exemple.fr"
                className="h-12 mt-1"
                data-testid="input-client-email"
              />
            </div>

            {/* Mode intervention après compteur */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <Label className="text-base font-semibold text-slate-800 mb-3 block">Type d&apos;intervention</Label>
              <p className="text-xs text-slate-500 mb-3">S&apos;agit-il d&apos;une intervention après compteur ?</p>
              <div className="flex gap-4">
                <label className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-all ${!formData.diagnostic_compteur ? "bg-sky-50 border-sky-300 ring-1 ring-sky-300" : "border-slate-200 hover:bg-slate-50"}`} data-testid="radio-compteur-non">
                  <input type="radio" name="diagnostic_compteur" checked={!formData.diagnostic_compteur} onChange={() => updateField("diagnostic_compteur", false)} className="accent-sky-600" />
                  <span className="text-sm font-medium">Non</span>
                </label>
                <label className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer transition-all ${formData.diagnostic_compteur ? "bg-sky-50 border-sky-300 ring-1 ring-sky-300" : "border-slate-200 hover:bg-slate-50"}`} data-testid="radio-compteur-oui">
                  <input type="radio" name="diagnostic_compteur" checked={formData.diagnostic_compteur} onChange={() => updateField("diagnostic_compteur", true)} className="accent-sky-600" />
                  <span className="text-sm font-medium">Oui</span>
                </label>
              </div>
            </div>

            {/* Partie B - Tiers concerné */}
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-base font-semibold text-slate-800">Partie B – Tiers concerné</Label>
                  <p className="text-xs text-slate-500 mt-1">Activer si un tiers est impliqué dans le sinistre</p>
                </div>
                <Switch
                  checked={hasPartyB}
                  onCheckedChange={setHasPartyB}
                  data-testid="toggle-party-b"
                />
              </div>

              {hasPartyB && (
                <div className="space-y-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm font-medium text-orange-800 mb-2">Identité du tiers</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="party_b_first_name">Prénom</Label>
                      <Input
                        id="party_b_first_name"
                        value={formData.party_b_first_name}
                        onChange={(e) => updateField("party_b_first_name", e.target.value)}
                        placeholder="Prénom"
                        className="h-12 mt-1"
                        data-testid="input-party-b-first-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="party_b_name">Nom</Label>
                      <Input
                        id="party_b_name"
                        value={formData.party_b_name}
                        onChange={(e) => updateField("party_b_name", e.target.value)}
                        placeholder="Nom"
                        className="h-12 mt-1"
                        data-testid="input-party-b-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="party_b_address">Adresse</Label>
                    <Input
                      id="party_b_address"
                      value={formData.party_b_address}
                      onChange={(e) => updateField("party_b_address", e.target.value)}
                      placeholder="Adresse du tiers"
                      className="h-12 mt-1"
                      data-testid="input-party-b-address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="party_b_phone">Téléphone</Label>
                    <Input
                      id="party_b_phone"
                      type="tel"
                      value={formData.party_b_phone}
                      onChange={(e) => updateField("party_b_phone", e.target.value)}
                      placeholder="06 12 34 56 78"
                      className="h-12 mt-1"
                      data-testid="input-party-b-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="party_b_email">Email</Label>
                    <Input
                      id="party_b_email"
                      type="email"
                      value={formData.party_b_email}
                      onChange={(e) => updateField("party_b_email", e.target.value)}
                      placeholder="email@exemple.fr"
                      className="h-12 mt-1"
                      data-testid="input-party-b-email"
                    />
                  </div>

                  {/* Relation avec le sinistre */}
                  <div>
                    <Label>Relation avec le sinistre</Label>
                    <Select
                      value={formData.party_b_relation}
                      onValueChange={(v) => updateField("party_b_relation", v)}
                    >
                      <SelectTrigger className="h-12 mt-1" data-testid="select-party-b-relation">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PARTY_B_RELATIONS.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.party_b_relation === "Autre" && (
                      <Input
                        value={formData.party_b_relation_other}
                        onChange={(e) => updateField("party_b_relation_other", e.target.value)}
                        placeholder="Préciser la relation..."
                        className="h-12 mt-2"
                        data-testid="input-party-b-relation-other"
                      />
                    )}
                  </div>

                  {/* Pièce touchée */}
                  <div>
                    <Label>Pièce touchée</Label>
                    <Select
                      value={formData.party_b_piece_touchee}
                      onValueChange={(v) => updateField("party_b_piece_touchee", v)}
                    >
                      <SelectTrigger className="h-12 mt-1" data-testid="select-party-b-piece">
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PIECES_TOUCHEES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.party_b_piece_touchee === "Autre" && (
                      <Input
                        value={formData.party_b_piece_touchee_other}
                        onChange={(e) => updateField("party_b_piece_touchee_other", e.target.value)}
                        placeholder="Préciser la pièce..."
                        className="h-12 mt-2"
                        data-testid="input-party-b-piece-other"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "intervention":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-sky-800">Lieu d'intervention</p>
            </div>

            {/* Same address toggle */}
            <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg bg-white" data-testid="same-address-toggle">
              <Label htmlFor="same-address" className="text-sm cursor-pointer pr-3">
                Le lieu d'intervention est identique à l'adresse du client
              </Label>
              <Switch
                id="same-address"
                checked={sameAddress}
                onCheckedChange={handleSameAddressToggle}
              />
            </div>

            <div>
              <Label htmlFor="address">Adresse d'intervention *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => updateField("address", e.target.value)}
                placeholder="Numéro et rue"
                className="h-12 mt-1"
                readOnly={sameAddress}
                data-testid="input-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="postal_code">Code postal</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => updateField("postal_code", e.target.value)}
                  placeholder="75000"
                  className="h-12 mt-1"
                  readOnly={sameAddress}
                />
              </div>
              <div>
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => updateField("city", e.target.value)}
                  placeholder="Paris"
                  className="h-12 mt-1"
                  readOnly={sameAddress}
                />
              </div>
            </div>
            <div>
              <Label>Type de local</Label>
              <Select
                value={formData.local_type}
                onValueChange={(v) => updateField("local_type", v)}
              >
                <SelectTrigger className="h-12 mt-1" data-testid="select-local-type">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {LOCAL_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="intervention_date">Date d'intervention</Label>
              <Input
                id="intervention_date"
                type="date"
                value={formData.intervention_date}
                onChange={(e) => updateField("intervention_date", e.target.value)}
                className="h-12 mt-1"
              />
            </div>
            <div>
              <Label htmlFor="report_writer">Rédacteur du rapport</Label>
              <Input
                id="report_writer"
                value={formData.report_writer}
                onChange={(e) => updateField("report_writer", e.target.value)}
                placeholder="Nom du rédacteur"
                className="h-12 mt-1"
              />
            </div>

            {/* Intervention status */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <Label>Intervention réalisée suite au diagnostic</Label>
              <Select
                value={formData.intervention_status}
                onValueChange={(v) => updateField("intervention_status", v)}
              >
                <SelectTrigger className="h-12 mt-1" data-testid="select-intervention-status">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui – intervention réalisée suite au diagnostic</SelectItem>
                  <SelectItem value="non">Non – diagnostic uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Service price */}
            <div>
              <Label htmlFor="service_price">Tarif de la prestation (€)</Label>
              <Input
                id="service_price"
                type="number"
                min="0"
                step="0.01"
                value={formData.service_price}
                onChange={(e) => updateField("service_price", e.target.value)}
                placeholder="Ex : 350.00"
                className="h-12 mt-1"
                data-testid="input-service-price"
              />
            </div>

            {/* Repair responsibility */}
            <div>
              <Label>Réparation réalisée par</Label>
              <Select
                value={formData.repair_responsibility}
                onValueChange={(v) => updateField("repair_responsibility", v)}
              >
                <SelectTrigger className="h-12 mt-1" data-testid="select-repair-responsibility">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="notre_societe">Notre société</SelectItem>
                  <SelectItem value="autre_societe">Une autre société</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Invoice number */}
            <div>
              <Label htmlFor="invoice_number">Numéro de facture</Label>
              <Input
                id="invoice_number"
                value={formData.invoice_number}
                onChange={(e) => updateField("invoice_number", e.target.value)}
                placeholder="Optionnel"
                className="h-12 mt-1"
                data-testid="input-invoice-number"
              />
            </div>
          </div>
        );

      case "sinister":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-amber-800">Description du sinistre</p>
            </div>
            <div>
              <Label htmlFor="sinister_description">Description du sinistre</Label>
              <Textarea
                id="sinister_description"
                value={formData.sinister_description}
                onChange={(e) => updateField("sinister_description", e.target.value)}
                placeholder="Décrivez le sinistre observé, son contexte et les circonstances..."
                className="mt-1 min-h-[150px]"
                data-testid="textarea-sinister"
              />
            </div>
          </div>
        );

      case "investigation":
        return (
          <InvestigationSteps
            investigations={formData.investigations}
            onChange={(investigations) => updateField("investigations", investigations)}
          />
        );

      case "diagnostic":
        return (
          <DiagnosticCompteur
            data={formData.diagnostic_compteur_data}
            onChange={(data) => updateField("diagnostic_compteur_data", data)}
          />
        );

      case "result":
        return (
          <div className="space-y-6">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="text-sm font-medium text-amber-800">Résultat de l'investigation</p>
            </div>
            
            <div>
              <Label>Résultat</Label>
              <div className="grid grid-cols-1 gap-3 mt-2">
                {RESULT_TYPES.map((result) => (
                  <div
                    key={result.value}
                    onClick={() => updateField("result_type", result.value)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.result_type === result.value
                        ? result.value === "fuite_constatee"
                          ? "border-red-500 bg-red-50"
                          : "border-green-500 bg-green-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.result_type === result.value
                          ? result.value === "fuite_constatee"
                            ? "border-red-500 bg-red-500"
                            : "border-green-500 bg-green-500"
                          : "border-slate-300"
                      }`}>
                        {formData.result_type === result.value && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <span className={`font-medium ${
                        formData.result_type === result.value
                          ? result.value === "fuite_constatee"
                            ? "text-red-700"
                            : "text-green-700"
                          : "text-slate-700"
                      }`}>
                        {result.label}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="conclusion">Conclusion technique</Label>
              <Textarea
                id="conclusion"
                value={formData.conclusion}
                onChange={(e) => updateField("conclusion", e.target.value)}
                placeholder="Décrivez les conclusions de votre investigation..."
                className="mt-1 min-h-[120px]"
              />
            </div>

            <div>
              <Label htmlFor="recommendations">Préconisations</Label>
              <Textarea
                id="recommendations"
                value={formData.recommendations}
                onChange={(e) => updateField("recommendations", e.target.value)}
                placeholder="Recommandations : remplacement, réparation, travaux..."
                className="mt-1 min-h-[120px]"
              />
            </div>
          </div>
        );

      case "signatures":
        return (
          <div className="space-y-8">
            <SignaturePad
              label="Signature du client"
              initialValue={formData.party_a_signature}
              onSave={(sig) => updateField("party_a_signature", sig)}
            />
            <SignaturePad
              label="Signature de l'intervenant"
              initialValue={formData.technician_signature}
              onSave={(sig) => updateField("technician_signature", sig)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="claim-form">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-slate-900">
              {isEdit ? "Modifier le dossier" : "Nouveau constat"}
            </h1>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(index)}
                  className="flex flex-col items-center gap-1"
                  data-testid={`step-${step.id}`}
                >
                  <div
                    className={`wizard-step-number ${
                      isActive ? "active" : isCompleted ? "completed" : "pending"
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <span
                    className={`text-xs ${
                      isActive ? "text-sky-600 font-medium" : "text-slate-400"
                    }`}
                  >
                    {step.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {(() => {
                const Icon = steps[currentStep].icon;
                return <Icon className="w-5 h-5 text-sky-600" />;
              })()}
              {steps[currentStep].title}
            </CardTitle>
          </CardHeader>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>
      </main>

      {/* Bottom Actions */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 z-20">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => setCurrentStep((prev) => Math.max(0, prev - 1))}
            disabled={currentStep === 0}
            className="h-12 px-6"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving}
              className="h-12 px-4"
              data-testid="save-draft-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>

            {currentStep < steps.length - 1 ? (
              <Button
                onClick={() => setCurrentStep((prev) => prev + 1)}
                disabled={!canProceed()}
                className="h-12 px-6 bg-sky-600 hover:bg-sky-700"
                data-testid="next-step-btn"
              >
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="h-12 px-6 bg-green-600 hover:bg-green-700"
                data-testid="finalize-btn"
              >
                {saving ? (
                  <div className="spinner w-5 h-5 border-white border-t-transparent" />
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Finaliser
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimForm;
